import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { saveTrade } from '../../services/storage';
import { THEME } from '../../styles/theme';

// MT4 History CSV columns (from Account History export):
// Ticket, Open Time, Type, Size, Item, Open Price, S/L, T/P, Close Time, Close Price, Commission, Taxes, Swap, Profit

// MT5 Deals CSV columns:
// Deal, Order, Symbol, Type, Direction, Volume, Price, Order, Commission, Fee, Swap, Profit, Balance, Comment, Time

function detectFormat(headers) {
  const h = headers.map(h => h.toLowerCase().trim());
  if (h.includes('ticket') && h.includes('open time')) return 'MT4';
  if (h.includes('deal') && h.includes('direction')) return 'MT5';
  if (h.includes('open_time') || h.includes('opentime')) return 'MT4_ALT';
  return 'UNKNOWN';
}

function parseMT4Row(row) {
  const profit = parseFloat(row['Profit'] || row['profit'] || 0);
  const entry  = parseFloat(row['Open Price'] || row['open_price'] || 0);
  const exit   = parseFloat(row['Close Price'] || row['close_price'] || 0);
  const sl     = parseFloat(row['S/L'] || row['sl'] || 0);
  const tp     = parseFloat(row['T/P'] || row['tp'] || 0);
  const size   = parseFloat(row['Size'] || row['size'] || row['Volume'] || 0);
  const type   = (row['Type'] || row['type'] || '').toLowerCase().trim();
  const symbol = (row['Item'] || row['item'] || row['Symbol'] || '').trim();
  const openTime = row['Open Time'] || row['open_time'] || '';
  const closeTime = row['Close Time'] || row['close_time'] || '';

  if (!['buy','sell','0','1'].includes(type) && !type.includes('buy') && !type.includes('sell')) return null;
  if (!symbol || (!symbol.includes('XAU') && !symbol.includes('GOLD') && !symbol.includes('GC'))) {
    // Include all symbols, mark non-gold
  }

  const direction = type.includes('buy') || type === '0' ? 'BUY' : 'SELL';
  const result    = profit > 0 ? 'WIN' : profit < 0 ? 'LOSS' : 'BREAKEVEN';

  // Calculate approximate R:R
  const slDist = entry && sl ? Math.abs(entry - sl) : 0;
  const tpDist = entry && tp ? Math.abs(tp - entry) : 0;
  const rr     = slDist > 0 && tpDist > 0 ? (tpDist / slDist).toFixed(1) : null;

  return {
    id:             `IMP${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
    date:           openTime ? openTime.split(' ')[0] : new Date().toISOString().split('T')[0],
    close_time:     closeTime,
    symbol:         symbol || 'XAUUSD',
    direction,
    entry:          entry || null,
    exit:           exit  || null,
    sl:             sl    || null,
    tp:             tp    || null,
    lot_size:       size,
    result,
    pnl_usd:        profit,
    rr_achieved:    rr,
    setup_type:     'IMPORTED',
    session:        detectSession(openTime),
    source:         'MT4/MT5 Import',
    notes:          `Imported from MT4/MT5. Ticket: ${row['Ticket'] || row['Deal'] || '—'}`,
  };
}

function detectSession(timeStr) {
  if (!timeStr) return 'Unknown';
  const hour = parseInt((timeStr.split(' ')[1] || '00').split(':')[0]);
  if (hour >= 2 && hour < 6)   return 'ASIAN';
  if (hour >= 7 && hour < 12)  return 'LONDON';
  if (hour >= 12 && hour < 17) return 'NEW_YORK';
  return 'OVERLAP';
}

const S = {
  wrap:      { padding: '20px', maxWidth: '1000px', margin: '0 auto' },
  title:     { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px', marginBottom: '6px' },
  sub:       { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '24px' },

  dropzone:  (drag) => ({ border: `2px dashed ${drag ? THEME.colors.blue : 'rgba(0,212,255,0.2)'}`, borderRadius: '12px', padding: '40px', textAlign: 'center', cursor: 'pointer', background: drag ? 'rgba(0,212,255,0.05)' : 'rgba(255,255,255,0.02)', transition: 'all 0.2s', marginBottom: '20px' }),

  statsBar:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' },
  statCard:  { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '12px' },
  statLbl:   { fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' },
  statVal:   (c='#fff') => ({ fontSize: '20px', fontWeight: '700', color: c, fontFamily: THEME.fonts.hud }),

  tableWrap: { overflowX: 'auto', marginBottom: '20px' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:        { textAlign: 'left', padding: '10px 12px', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap' },
  td:        { padding: '9px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap' },
  trEven:    { background: 'rgba(255,255,255,0.01)' },

  btnRow:    { display: 'flex', gap: '10px', marginTop: '16px' },
  btn:       (col=THEME.colors.blue) => ({ padding: '11px 24px', borderRadius: '8px', border: 'none', background: col, color: '#000', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }),
  btnGhost:  { padding: '11px 20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '13px' },

  progress:  { background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '10px', padding: '16px', marginBottom: '16px' },
  error:     { color: '#ef4444', padding: '12px', background: '#ef444411', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' },
  success:   { color: '#10b981', fontWeight: '700', fontSize: '14px' },

  guide:     { background: 'rgba(255,255,255,0.02)', borderRadius: '10px', padding: '20px', fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.9' },
};

export default function TradeImport() {
  const [trades,   setTrades]   = useState([]);
  const [format,   setFormat]   = useState('');
  const [error,    setError]    = useState('');
  const [importing,setImporting]= useState(false);
  const [imported, setImported] = useState(0);
  const [drag,     setDrag]     = useState(false);
  const [filter,   setFilter]   = useState('ALL');
  const fileRef = useRef();

  const loadCSV = (file) => {
    if (!file) return;
    setError('');
    setTrades([]);
    setImported(0);

    Papa.parse(file, {
      header:    true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data.length) { setError('CSV is empty'); return; }

        const fmt = detectFormat(results.meta.fields || []);
        setFormat(fmt);

        if (fmt === 'UNKNOWN') {
          setError('Unrecognized format. Supported: MT4 Account History CSV, MT5 Deals CSV');
          return;
        }

        const parsed = results.data
          .map(parseMT4Row)
          .filter(Boolean)
          .filter(t => t.result !== 'BREAKEVEN' || t.pnl_usd !== 0); // filter empty rows

        if (!parsed.length) {
          setError('No valid trades found. Make sure the CSV has Buy/Sell trades with profit.');
          return;
        }
        setTrades(parsed);
      },
      error: (err) => setError('CSV parse error: ' + err.message),
    });
  };

  const importAll = async () => {
    setImporting(true);
    let count = 0;
    const toImport = trades.filter(t => filter === 'ALL' ? true : filter === 'XAUUSD' ? t.symbol.includes('XAU') || t.symbol.includes('GOLD') : true);

    for (const trade of toImport) {
      saveTrade({
        direction:       trade.direction,
        entry:           trade.entry,
        exit:            trade.exit,
        sl:              trade.sl,
        tp:              trade.tp,
        lot_size:        trade.lot_size,
        result:          trade.result,
        pnl_usd:         trade.pnl_usd,
        rr_achieved:     trade.rr_achieved,
        setup_type:      trade.setup_type,
        session:         trade.session,
        date:            trade.date,
        notes:           trade.notes,
        source:          'MT4/MT5 Import',
        symbol:          trade.symbol,
      });
      count++;
      if (count % 10 === 0) setImported(count);
      await new Promise(r => setTimeout(r, 10));
    }
    setImported(count);
    setImporting(false);
  };

  const wins    = trades.filter(t => t.result === 'WIN');
  const losses  = trades.filter(t => t.result === 'LOSS');
  const winRate = trades.length ? Math.round(wins.length / trades.length * 100) : 0;
  const totalPnL= trades.reduce((s, t) => s + t.pnl_usd, 0);
  const xauTrades = trades.filter(t => t.symbol.includes('XAU') || t.symbol.includes('GOLD'));

  const displayed = filter === 'XAUUSD' ? xauTrades : trades;

  return (
    <div style={S.wrap}>
      <div style={S.title}>MT4 / MT5 TRADE IMPORT</div>
      <div style={S.sub}>
        Import your complete trading history from MetaTrader. JARVIS will instantly learn from all your past trades and update the Agent Network.
      </div>

      {error && <div style={S.error}>{error}</div>}

      {/* Drop zone */}
      {!trades.length && (
        <>
          <div
            style={S.dropzone(drag)}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); loadCSV(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current.click()}
          >
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📊</div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#e2e8f0', marginBottom: '4px' }}>
              Drop MT4/MT5 CSV here or click to upload
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
              MT4: Account History → Save as Report → .csv &nbsp;|&nbsp; MT5: History → Export to .csv
            </div>
            <input ref={fileRef} type="file" accept=".csv,.htm,.html" style={{ display: 'none' }}
              onChange={e => loadCSV(e.target.files[0])} />
          </div>

          <div style={S.guide}>
            <div style={{ color: THEME.colors.blue, fontWeight: '700', marginBottom: '10px', fontSize: '13px' }}>HOW TO EXPORT FROM MT4:</div>
            1. Open MetaTrader 4<br/>
            2. Click <b style={{ color: '#e2e8f0' }}>View → Terminal</b> (Ctrl+T)<br/>
            3. Click <b style={{ color: '#e2e8f0' }}>Account History</b> tab<br/>
            4. Right-click → <b style={{ color: '#e2e8f0' }}>Save as Report</b><br/>
            5. Save as <b style={{ color: '#e2e8f0' }}>CSV (comma separated)</b><br/>
            6. Upload that file here ↑<br/><br/>
            <div style={{ color: THEME.colors.blue, fontWeight: '700', marginBottom: '10px', fontSize: '13px' }}>HOW TO EXPORT FROM MT5:</div>
            1. Open MetaTrader 5<br/>
            2. Click <b style={{ color: '#e2e8f0' }}>View → Toolbox</b><br/>
            3. Click <b style={{ color: '#e2e8f0' }}>History</b> tab<br/>
            4. Right-click → <b style={{ color: '#e2e8f0' }}>Export all to CSV</b>
          </div>
        </>
      )}

      {/* Stats bar */}
      {trades.length > 0 && !imported && (
        <>
          <div style={S.statsBar}>
            {[
              { label: 'TOTAL TRADES', value: trades.length,           color: '#fff' },
              { label: 'WIN RATE',     value: winRate + '%',            color: winRate >= 50 ? '#10b981' : '#ef4444' },
              { label: 'TOTAL P&L',   value: (totalPnL >= 0 ? '+$' : '-$') + Math.abs(totalPnL).toFixed(0), color: totalPnL >= 0 ? '#10b981' : '#ef4444' },
              { label: 'WINS',         value: wins.length,              color: '#10b981' },
              { label: 'LOSSES',       value: losses.length,            color: '#ef4444' },
              { label: 'XAUUSD TRADES',value: xauTrades.length,         color: THEME.colors.blue },
              { label: 'FORMAT',       value: format,                   color: '#f59e0b' },
            ].map(s => (
              <div key={s.label} style={S.statCard}>
                <div style={S.statLbl}>{s.label}</div>
                <div style={S.statVal(s.color)}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Filter */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {['ALL', 'XAUUSD'].map(f => (
              <button key={f} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${filter === f ? THEME.colors.blue : 'rgba(255,255,255,0.1)'}`, background: filter === f ? 'rgba(0,212,255,0.1)' : 'transparent', color: filter === f ? THEME.colors.blue : '#9ca3af', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }} onClick={() => setFilter(f)}>
                {f === 'ALL' ? `All Symbols (${trades.length})` : `XAUUSD/Gold Only (${xauTrades.length})`}
              </button>
            ))}
          </div>

          {/* Preview table */}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['DATE','SYMBOL','DIRECTION','ENTRY','EXIT','LOT','P&L ($)','RESULT','SESSION'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.slice(0, 50).map((t, i) => (
                  <tr key={t.id} style={i % 2 === 0 ? S.trEven : {}}>
                    <td style={S.td}>{t.date}</td>
                    <td style={S.td}>{t.symbol}</td>
                    <td style={{ ...S.td, color: t.direction === 'BUY' ? '#10b981' : '#ef4444', fontWeight: '700' }}>{t.direction}</td>
                    <td style={S.td}>{t.entry?.toFixed(2) || '—'}</td>
                    <td style={S.td}>{t.exit?.toFixed(2)  || '—'}</td>
                    <td style={S.td}>{t.lot_size}</td>
                    <td style={{ ...S.td, color: t.pnl_usd >= 0 ? '#10b981' : '#ef4444', fontWeight: '700' }}>
                      {t.pnl_usd >= 0 ? '+' : ''}{t.pnl_usd.toFixed(2)}
                    </td>
                    <td style={{ ...S.td, color: t.result === 'WIN' ? '#10b981' : '#ef4444' }}>{t.result}</td>
                    <td style={{ ...S.td, color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>{t.session}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {displayed.length > 50 && (
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', padding: '8px 12px' }}>
                Showing first 50 of {displayed.length} trades
              </div>
            )}
          </div>

          <div style={S.btnRow}>
            <button style={S.btn()} onClick={importAll} disabled={importing}>
              {importing ? `Importing... (${imported}/${displayed.length})` : `Import ${displayed.length} Trades to JARVIS`}
            </button>
            <button style={S.btnGhost} onClick={() => { setTrades([]); setFormat(''); }}>
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Import complete */}
      {imported > 0 && !importing && (
        <div style={S.progress}>
          <div style={S.success}>
            ✅ {imported} trades imported successfully!
          </div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>
            JARVIS now has {imported} more trades to learn from.
            Go to <b style={{ color: THEME.colors.blue }}>Agent Network</b> to see how strategies evolve with this new data.
            Check <b style={{ color: THEME.colors.blue }}>Stats & Performance</b> for your updated win rate.
          </div>
          <button style={{ ...S.btnGhost, marginTop: '12px' }} onClick={() => { setTrades([]); setImported(0); }}>
            Import Another File
          </button>
        </div>
      )}
    </div>
  );
}
